import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-filter-bar.css?inline';

/**
 * `<pk-filter-bar>` — composite filter UI used above lists/tables.
 *
 * Three slot regions: `search` (free-text search input), `facets` (facet
 * pills / selects), `actions` (right-aligned CTA buttons like "Export CSV").
 *
 * Listens to bubbled `input`/`change` events from slotted controls and emits
 * a debounced `filter-change` (CustomEvent, bubbles, composed) with a snapshot
 * of the current filter state — a record of `[name, value]` pairs gathered from
 * any slotted form control that carries a `name` attribute. The default
 * debounce is 200ms; override via `debounce-ms`.
 *
 * Attributes:
 *   - `dense`       — boolean; reduces vertical padding for tight surfaces.
 *   - `debounce-ms` — debounce window in ms (default 200).
 */
export class PkFilterBar extends HTMLElement {
    static readonly tagName = 'pk-filter-bar';

    static get observedAttributes(): string[] {
        return ['dense', 'debounce-ms'];
    }

    private readonly _root: ShadowRoot;
    private _timer: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
        this.addEventListener('input',  this._scheduleEmit);
        this.addEventListener('change', this._scheduleEmit);
    }

    disconnectedCallback(): void {
        this.removeEventListener('input',  this._scheduleEmit);
        this.removeEventListener('change', this._scheduleEmit);
        if (this._timer !== null) {
            clearTimeout(this._timer);
            this._timer = null;
        }
    }

    attributeChangedCallback(): void {
        this.render();
    }

    /**
     * Snapshot of the current filter state. Reads `name` + `value` pairs from
     * any slotted control (or descendant) that carries a `name` attribute.
     * Inputs of type `checkbox` and `radio` are only included when checked.
     * Useful for tests and for consumers that want to read state on demand.
     */
    snapshot(): Record<string, string> {
        const result: Record<string, string> = {};
        const named = this.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
            '[name]',
        );
        named.forEach((el) => {
            const name = el.getAttribute('name');
            if (!name) return;
            // Filter out helper elements like buttons.
            if (el instanceof HTMLInputElement) {
                if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
                result[name] = el.value;
            } else if (el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
                result[name] = el.value;
            } else if ('value' in el && typeof (el as { value: unknown }).value === 'string') {
                result[name] = (el as { value: string }).value;
            }
        });
        return result;
    }

    private _debounceMs(): number {
        const raw = Number(this.getAttribute('debounce-ms'));
        return Number.isFinite(raw) && raw > 0 ? raw : 200;
    }

    private readonly _scheduleEmit = (): void => {
        if (this._timer !== null) clearTimeout(this._timer);
        this._timer = setTimeout(() => {
            this._timer = null;
            this.dispatchEvent(new CustomEvent<Record<string, string>>('filter-change', {
                detail:   this.snapshot(),
                bubbles:  true,
                composed: true,
            }));
        }, this._debounceMs());
    };

    private template(): TemplateResult {
        const dense = this.hasAttribute('dense');
        return html`
            <style>${styles}</style>
            <div class="filter-bar ${dense ? 'dense' : ''}">
                <div class="search">
                    <slot name="search"></slot>
                </div>
                <div class="facets">
                    <slot name="facets"></slot>
                </div>
                <div class="actions">
                    <slot name="actions"></slot>
                </div>
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkFilterBar.tagName)) {
    customElements.define(PkFilterBar.tagName, PkFilterBar);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-filter-bar': PkFilterBar;
    }
}
